import React, {useState, useCallback} from 'react';

interface ModalContext {
    showModal: (renderFn: (props: {show: boolean}) => React.ReactNode) => void;
    closeModal: () => void;
}

/**
 * Hook for managing local modal state within a component.
 * 
 * Usage:
 * ```tsx
 * const [modalContext, modalContent] = useLocalModal();
 * 
 * const handleClick = () => {
 *   modalContext.showModal(({show}) => (
 *     <Dialog open={show}>
 *       <p>Modal content</p>
 *       <button onClick={modalContext.closeModal}>Close</button>
 *     </Dialog>
 *   ));
 * };
 * 
 * return (
 *   <>
 *     <button onClick={handleClick}>Open Modal</button>
 *     {modalContent}
 *   </>
 * );
 * ```
 */
export const useLocalModal = (): [ModalContext, React.ReactNode] => {
    const [content, setContent] = useState<React.ReactNode>(null);

    const showModal = useCallback((renderFn: (props: {show: boolean}) => React.ReactNode) => {
        setContent(renderFn({show: true}));
    }, []);

    const closeModal = useCallback(() => {
        setContent(null);
    }, []);

    const modalContext: ModalContext = {showModal, closeModal};

    return [modalContext, content];
};
